import { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Paperclip, X } from "lucide-react";
import { format } from "date-fns";
import type { Calendar, CalendarEvent } from "@/hooks/useCalendar";
import {
  useCreateCalendarEvent,
  useUpdateCalendarEvent,
} from "@/hooks/useCalendar";
import { useUploadCalendarAttachment } from "@/hooks/useCalendarAttachments";
import { supabase } from "@/integrations/supabase/client";

const OOO_SLUG = "cal_ooo";

interface CreateCalendarEventDialogProps {
  calendars: Calendar[];
  editEvent?: CalendarEvent | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultDate?: Date | null;
  defaultCalendarId?: string;
}

export function CreateCalendarEventDialog({
  calendars,
  editEvent,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  defaultDate,
  defaultCalendarId,
}: CreateCalendarEventDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled
    ? (v: boolean) => externalOnOpenChange?.(v)
    : setInternalOpen;

  // Form state
  const [calendarId, setCalendarId] = useState("");
  const [title, setTitle] = useState("");
  const [clientOrBrand, setClientOrBrand] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("10:00");
  const [status, setStatus] = useState<"planned" | "confirmed" | "cancelled">("planned");
  const [headcount, setHeadcount] = useState("");
  const [notes, setNotes] = useState("");

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateCalendarEvent();
  const updateMutation = useUpdateCalendarEvent();
  const uploadAttachment = useUploadCalendarAttachment();
  const isEditing = !!editEvent;
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Detect OOO calendar selection
  const selectedCalendar = useMemo(
    () => calendars.find((c) => c.id === calendarId),
    [calendars, calendarId]
  );
  const isOOO = selectedCalendar?.slug === OOO_SLUG;

  const resetForm = () => {
    const today = defaultDate ? format(defaultDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");
    setCalendarId(defaultCalendarId || calendars.find((c) => c.slug !== OOO_SLUG)?.id || calendars[0]?.id || "");
    setTitle("");
    setClientOrBrand("");
    setAllDay(false);
    setStartDate(today);
    setStartTime("09:00");
    setEndDate(today);
    setEndTime("10:00");
    setStatus("planned");
    setHeadcount("");
    setNotes("");
    setPendingFiles([]);
  };

  useEffect(() => {
    if (editEvent && open) {
      setCalendarId(editEvent.calendar_id);
      setTitle(editEvent.title);
      setClientOrBrand(editEvent.client_or_brand || "");
      setAllDay(editEvent.all_day);
      const start = new Date(editEvent.start_at);
      const end = new Date(editEvent.end_at);
      setStartDate(format(start, "yyyy-MM-dd"));
      setStartTime(format(start, "HH:mm"));
      setEndDate(format(end, "yyyy-MM-dd"));
      setEndTime(format(end, "HH:mm"));
      setStatus(editEvent.status);
      setHeadcount(editEvent.required_headcount?.toString() || "");
      setNotes(editEvent.notes || "");
      setPendingFiles([]);
    } else if (!editEvent && open) {
      resetForm();
    }
  }, [editEvent, open]);

  useEffect(() => {
    if (defaultDate && !editEvent && !open) {
      setStartDate(format(defaultDate, "yyyy-MM-dd"));
      setEndDate(format(defaultDate, "yyyy-MM-dd"));
    }
  }, [defaultDate]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const startAt = allDay
      ? `${startDate}T00:00:00`
      : `${startDate}T${startTime}:00`;
    const endAt = allDay
      ? `${endDate || startDate}T23:59:59`
      : `${endDate || startDate}T${endTime}:00`;

    const payload = {
      calendar_id: calendarId,
      title,
      client_or_brand: isOOO ? undefined : (clientOrBrand || undefined),
      start_at: new Date(startAt).toISOString(),
      end_at: new Date(endAt).toISOString(),
      all_day: isOOO ? false : allDay,
      status,
      required_headcount: isOOO ? undefined : (headcount ? parseInt(headcount, 10) : undefined),
      notes: notes || undefined,
    };

    if (isEditing) {
      updateMutation.mutate(
        { id: editEvent!.id, input: payload },
        { onSuccess: () => setOpen(false) }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: async (newEvent) => {
          if (pendingFiles.length > 0 && newEvent) {
            const { data: ua } = await supabase
              .from("user_account")
              .select("org_id")
              .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
              .single();
            const orgId = (ua as any)?.org_id;
            if (orgId) {
              await Promise.all(
                pendingFiles.map((file) =>
                  uploadAttachment.mutateAsync({ file, eventId: newEvent.id, orgId })
                )
              );
            }
          }
          resetForm();
          setOpen(false);
        },
      });
    }
  };

  const dialogContent = (
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{isEditing ? "Edit Event" : "New Event"}</DialogTitle>
        <DialogDescription>
          {isEditing
            ? "Update the calendar event details."
            : "Add a new event to the calendar."}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Calendar selector — always visible */}
        <div className="space-y-2">
          <Label>Calendar</Label>
          <Select value={calendarId} onValueChange={setCalendarId}>
            <SelectTrigger>
              <SelectValue placeholder="Select calendar" />
            </SelectTrigger>
            <SelectContent>
              {calendars.map((cal) => (
                <SelectItem key={cal.id} value={cal.id}>
                  <div className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: cal.color }}
                    />
                    {cal.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Title — always visible */}
        <div className="space-y-2">
          <Label>Title *</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event title"
            required
          />
        </div>

        {/* Fields hidden when OOO calendar is selected */}
        {!isOOO && (
          <>
            <div className="space-y-2">
              <Label>Client / Brand</Label>
              <Input
                value={clientOrBrand}
                onChange={(e) => setClientOrBrand(e.target.value)}
                placeholder="Client or brand name"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>All Day</Label>
              <Switch checked={allDay} onCheckedChange={setAllDay} />
            </div>
          </>
        )}

        {/* Date & Time — always visible */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Date *</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
          {!allDay && (
            <div className="space-y-2">
              <Label>Start Time *</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {!allDay && (
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Status — always visible */}
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="planned">Planned</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Fields hidden when OOO */}
        {!isOOO && (
          <div className="space-y-2">
            <Label>Required Headcount</Label>
            <Input
              type="number"
              min="0"
              value={headcount}
              onChange={(e) => setHeadcount(e.target.value)}
              placeholder="Number of people needed"
            />
          </div>
        )}

        {/* Notes — always visible */}
        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            rows={3}
          />
        </div>

        {/* Attachments — hidden when OOO */}
        {!isOOO && !isEditing && (
          <div className="space-y-2">
            <Label>Attachments</Label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4 mr-2" />
              Attach Files
            </Button>
            {pendingFiles.length > 0 && (
              <ul className="space-y-1 mt-2">
                {pendingFiles.map((file, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between text-sm bg-muted rounded px-2 py-1"
                  >
                    <span className="truncate max-w-[280px]">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="ml-2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending || !title || !calendarId}>
            {isPending
              ? isEditing ? "Saving..." : "Creating..."
              : isEditing ? "Save Changes" : "Create Event"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );

  if (isControlled) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {dialogContent}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4 mr-2" />
        New Event
      </Button>
      {dialogContent}
    </Dialog>
  );
}
